import React from "react";
import {
  DropzoneFormik,
  IDropzoneUploaderFile,
  InputLabel,
  LoadingIndicator,
  ValidationMessage,
} from "component-library";
import { ERROR } from "component-library/src/components/Atoms/InputsCommon/InputWrapper";
import { JoiningPage } from "translations/src/models/joining-pages";

import { useTranslations } from "../../../../../hooks/useTranslations";
import {
  acceptedHeadshotFileTypes,
  HEADSHOT_MAX_SIZE,
  HEADSHOT_MIN_HEIGHT,
  HEADSHOT_MIN_WIDTH,
} from "./UploadImageValidation";

interface IHeadshotDropzoneProps {
  setPhoto: (photo: string | IDropzoneUploaderFile[]) => void;
  showUploadModal: () => void;
  headshotFormError: boolean;
  headshotUploadError: boolean;
  submittingHeadshot: boolean;
  isYP?: boolean;
}

interface IUploadingMessageProps {
  uploadText: string;
}

const UploadingMessage: React.FC<IUploadingMessageProps> = ({ uploadText }) => (
  <div className="relative mb-10">
    <div className="flex items-center justify-center w-full h-12 border border-dashed border-grey-light-2 bg-color-neutral-three opacity-60" />
    <div className="absolute top-0 flex items-center justify-center w-full h-full">
      <div className="flex items-center space-x-2">
        <LoadingIndicator variant="secondary" />
        <h3 className="text-sm">{uploadText}</h3>
      </div>
    </div>
  </div>
);

const HeadshotDropzone: React.FC<IHeadshotDropzoneProps> = ({
  setPhoto,
  showUploadModal,
  headshotFormError,
  headshotUploadError,
  submittingHeadshot,
  isYP,
}) => {
  const { texts: commonTexts } = useTranslations(JoiningPage.Common);
  const commonTranslations = commonTexts.common;
  const { texts } = useTranslations(JoiningPage.AboutYouPage);
  const pageTexts = texts.mainPage;

  const onHeadshotDrop = (files: IDropzoneUploaderFile[]) => {
    setPhoto(files);
    showUploadModal();
  };

  return (
    <fieldset className="border-b border-grey-light-2">
      <InputLabel label={pageTexts.uploadHeadshotLabel} bold />
      <p className="mb-5">{pageTexts.headshotDescription}</p>

      <div>
        <DropzoneFormik
          name="photo"
          onDrop={onHeadshotDrop}
          placeholder={commonTranslations.dropzonePlaceholder}
          customFileList={() => null}
          errorModalTexts={commonTexts.errorsCommon}
          imageDimensions={{
            minWidth: HEADSHOT_MIN_WIDTH,
            minHeight: HEADSHOT_MIN_HEIGHT,
          }}
          maxSize={HEADSHOT_MAX_SIZE}
          acceptedFileTypes={acceptedHeadshotFileTypes}
          errorButtonId={`btn${isYP ? "-yp" : ""}-headshot-file-error-close`}
        />
      </div>

      {submittingHeadshot && (
        <UploadingMessage uploadText={commonTranslations.uploadingMessage} />
      )}

      {headshotFormError && (
        <div className="h-6 -mt-4">
          <ValidationMessage
            message={pageTexts.uploadHeadshotError}
            type={ERROR}
          />
        </div>
      )}

      {!headshotFormError && headshotUploadError && (
        <div className="h-6 -mt-4">
          <ValidationMessage
            message={commonTexts.errorsCommon.failedToUploadFile}
            type={ERROR}
          />
        </div>
      )}
    </fieldset>
  );
};

export default HeadshotDropzone;
